import React, { useEffect, useMemo } from 'react';
import I18n from 'modules/i18n';
import Spinner from '@/components/common/Spinner';
import { FilterCategory } from '@/components/common/libraries/common/Filter';
import Text from '@/components/ui/Text';
import useStandardsSearch from '../hooks/useStandardsSearch';
import useStandardsSelection from '../hooks/useStandardsSelection';
import { useStandardsRoots } from '../hooks/useStandardsTree';
import SectionHeading from '../SectionHeading';
import TruncatedCorrelationsList from '../TruncatedCorrelationsList';
import useCorrelationsStore from '../useCorrelationsStore';
import SeeMoreButton from './SeeMoreButton';
import StandardsSearch from './StandardsSearch';
import StandardsSearchResults from './StandardsSearchResults';
import StandardsTreeNode from './StandardsTreeNode';

const COLLAPSED_MAX_HEIGHT = 215;

interface StandardsFilterProps {
  grade: string | number | null;
  programId: number;
  stateId: number;
}

const Message = ({ children }: { children: React.ReactNode }) => (
  <Text className="tw-text-very-dark-grey tw-px-4" size="sm">{children}</Text>
);

const NoSearchResults = () => (
  <div className="tw-px-4 tw-py-2">
    <Text className="tw-text-tci-black tw-mb-1" size="sm" weight="bold">
      {I18n.t('no_standards_match_your_search')}
    </Text>
    <Text className="tw-text-very-dark-grey" size="xs">{I18n.t('check_spelling_or_try_code')}</Text>
  </div>
);

const StandardsFilter = ({ grade, programId, stateId }: StandardsFilterProps) => {
  const autoExpandStandards = useCorrelationsStore(s => s.autoExpandStandards);
  const categoryExpanded = useCorrelationsStore(s => s.categoryExpanded);
  const setCategoryExpanded = useCorrelationsStore(s => s.setCategoryExpanded);
  const { checkboxStateFor, checkFailed, handleCheck } = useStandardsSelection({ grade, programId, stateId });

  const {
    fetchNextPage, hasNextPage, isError, isFetchingNextPage, isInitialLoading, nodes: roots,
  } = useStandardsRoots({ enabled: true, grade, programId, stateId });

  const {
    isError: searchIsError, isLoading: searchIsLoading, nodes: searchNodes, searching, term,
  } = useStandardsSearch({ grade, programId, stateId });
  const searchNoResults = searching && searchNodes?.length === 0;

  const initKey = `${stateId}:${programId}:${grade ?? ''}`;

  // A search term swap can replace the row DOM without changing the list height (no ResizeObserver
  // fire), so the term is part of the remeasure signal alongside sibling-section toggles.
  const remeasureKey = useMemo(
    () => ({ categoryExpanded, searching, term }),
    [categoryExpanded, searching, term],
  );

  useEffect(() => {
    if (!roots) return;

    autoExpandStandards(roots.filter(root => root.has_children).map(root => root.id), initKey);
  }, [roots, initKey, autoExpandStandards]);

  return (
    <FilterCategory
      className="tw-pr-4 tw-mb-1"
      shouldCollapse={false}
      triggerElement={<SectionHeading>{I18n.t('standards')}</SectionHeading>}
    >
      {isInitialLoading && <Spinner className="tw-py-2" />}
      {(isError || checkFailed) && <Message>{I18n.t('error_loading_standards')}</Message>}
      {roots && roots.length === 0 && <Message>{I18n.t('no_standards_for_program')}</Message>}
      {roots && roots.length > 0 && (
        <>
          <StandardsSearch />
          {/* AIDEV-NOTE: live region must already be in the DOM when the message lands, so it stays
              mounted and only its content toggles (see .claude/docs/patterns-frontend.md, PLAT-17759) */}
          <div role="status">
            {searchNoResults && <NoSearchResults />}
          </div>
          {!searchNoResults && (
            <div aria-label={I18n.t('standards')} className="tw-pl-2" role="group">
              <TruncatedCorrelationsList
                collapsedMaxHeight={COLLAPSED_MAX_HEIGHT}
                expanded={categoryExpanded.standards}
                remeasureKey={remeasureKey}
                onToggle={next => setCategoryExpanded('standards', next)}
              >
                {/* The list lives inside the clip container, not around it, so the rows stay
                    direct children of the <ul> and keep their list semantics (PLAT-17901). */}
                <ul role="list">
                  {searching ? (
                    <>
                      {searchIsLoading && <li><Spinner className="tw-py-2" /></li>}
                      {searchIsError && <li><Message>{I18n.t('error_loading_standards')}</Message></li>}
                      {searchNodes && (
                        <StandardsSearchResults
                          key={term}
                          getCheckboxState={checkboxStateFor}
                          nodes={searchNodes}
                          onCheck={handleCheck}
                        />
                      )}
                    </>
                  ) : (
                    <>
                      {roots.map(root => (
                        <StandardsTreeNode
                          key={root.id}
                          getCheckboxState={checkboxStateFor}
                          grade={grade}
                          node={root}
                          programId={programId}
                          stateId={stateId}
                          onCheck={handleCheck}
                        />
                      ))}
                      {hasNextPage && (
                        <SeeMoreButton
                          label={I18n.t('standards')}
                          loading={isFetchingNextPage}
                          style={{ paddingLeft: '20px' }}
                          onClick={() => fetchNextPage()}
                        />
                      )}
                    </>
                  )}
                </ul>
              </TruncatedCorrelationsList>
            </div>
          )}
        </>
      )}
    </FilterCategory>
  );
};

export default StandardsFilter;
